import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router';
import { useState } from 'react';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Page } from '@/components/Page';
import { SectionHeader } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import {
    SurveyPermissionEditor,
    SurveyPermissionEditorSkeleton,
} from '@/components/surveys/permissions/SurveyPermissionEditor';
import { SurveySelector, SurveySelectorSkeleton } from '@/components/surveys/permissions/SurveySelector';
import { fetchSurveys } from '@/queries/surveys/system';
import { fetchGeneralUser } from '@/queries/systemGeneralUsers';
import { getFullName } from '@/utils';
import {
    useRevokeUserSurveyPermission,
    useSaveUserSurveyPermission,
    useSurveyAttributes,
    useUserSurveyPermissions,
} from './-hooks';
import * as styles from './index.css';

export const Route = createFileRoute('/system/_auth/organizations/$organizationId/users/$userId/survey-permissions/')({
    component: SystemUserSurveyPermissionsRoute,
    loader: async ({ params }) => {
        const [user, surveys] = await Promise.all([
            fetchGeneralUser(params.organizationId, params.userId),
            fetchSurveys(params.organizationId),
        ]);
        return { user, surveys: surveys.surveys };
    },
    pendingComponent: SystemUserSurveyPermissionsRouteSkeleton,
    errorComponent: SystemUserSurveyPermissionsRouteError,
});

function SystemUserSurveyPermissionsRouteError({ reset }: ErrorComponentProps) {
    return <ErrorOverlay active onRetry={reset} />;
}

/**
 * Bails out before `Content` mounts — and its permission/attribute queries
 * fire — when there are no surveys to show.
 */
function SystemUserSurveyPermissionsRoute() {
    const { surveys } = Route.useLoaderData();

    if (surveys.length === 0) {
        return (
            <>
                <Page.BackLink to="/system/organizations/$organizationId/users" label="ユーザー管理に戻る" />
                <p className={styles.emptyState}>公開中のサーベイがありません。</p>
            </>
        );
    }

    return <Content />;
}

function Content() {
    const { organizationId, userId } = Route.useParams();
    const { user, surveys } = Route.useLoaderData();
    const [selectedSurveyId, setSelectedSurveyId] = useState(surveys[0]?.surveyId ?? '');

    const attributesQuery = useSurveyAttributes(organizationId, selectedSurveyId);
    const permissionsQuery = useUserSurveyPermissions(organizationId, userId);
    const saveMutation = useSaveUserSurveyPermission(organizationId, userId);
    const revokeMutation = useRevokeUserSurveyPermission(organizationId, userId);

    const selectedSurvey = surveys.find((survey) => survey.surveyId === selectedSurveyId) ?? surveys[0];
    const isSaving = saveMutation.isPending || revokeMutation.isPending;

    return (
        <>
            <Page.BackLink to="/system/organizations/$organizationId/users" label="ユーザー管理に戻る" />

            <header className={styles.name}>{`${getFullName(user)} の閲覧権限`}</header>

            <SurveySelector
                surveys={surveys}
                permissions={permissionsQuery}
                selectedSurveyId={selectedSurveyId}
                onSelectSurvey={setSelectedSurveyId}
                disabled={isSaving}
            />

            <SurveyPermissionEditor
                survey={selectedSurvey}
                attributes={attributesQuery}
                permissions={permissionsQuery}
                isSaving={isSaving}
                onSave={(surveyId, input) => saveMutation.mutate({ surveyId, input })}
                onRevoke={(surveyId) => revokeMutation.mutate(surveyId)}
            />
        </>
    );
}

function SystemUserSurveyPermissionsRouteSkeleton() {
    return (
        <>
            <Page.BackLink to="/system/organizations/$organizationId/users" label="ユーザー管理に戻る" />

            <SectionHeader title={<Skeleton.Line width={200} height="xl" />} />

            <Page.Content>
                <SurveySelectorSkeleton />

                <SurveyPermissionEditorSkeleton />
            </Page.Content>
        </>
    );
}
